import { PageDataTable } from "components/PageDataTable/PageDataTable";
import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { InputSwitch } from "primereact/inputswitch";
import { TabPanel, TabView } from "primereact/tabview";
import { Tag } from "primereact/tag";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IDraftRunSummary,
  useDeleteContractModelDraftRunMutation,
  useListContractModelDraftRunsQuery,
} from "store/queries/contractModelDrafts";

import { NewContractModelDialog } from "./NewContractModelDialog";
import { StandingInstructionsTab } from "./StandingInstructionsTab";
import {
  canDeleteRun,
  formatRunDate,
  runDisplayName,
  runStatusLabel,
  runStatusSeverity,
} from "./utils";

// Module-level table cell renderers (not defined during render).
const nameBody = (r: IDraftRunSummary) => (
  <span className="font-medium">{runDisplayName(r)}</span>
);
const statusBody = (r: IDraftRunSummary) => (
  <Tag
    value={runStatusLabel(r.status)}
    severity={runStatusSeverity(r.status)}
  />
);
const createdBody = (r: IDraftRunSummary) => formatRunDate(r.createdAt);

/** The API's message on a refusal (e.g. the 409 naming which delete condition failed). */
const errorDetail = (e: unknown): string => {
  const apiMessage = (e as { data?: { message?: string } })?.data?.message;
  return typeof apiMessage === "string" && apiMessage.length > 0
    ? apiMessage
    : "Something went wrong. Please try again.";
};

export function ContractModelDrafts() {
  const navigate = useNavigate();

  const toastRef = useContext(ToastContext);
  // Canceled, failed and dead runs are hidden by the server unless asked for.
  const [showInactive, setShowInactive] = useState(false);
  const { data: runs = [] } = useListContractModelDraftRunsQuery({
    includeInactive: showInactive,
  });
  const [deleteRun, { isLoading: deleting }] =
    useDeleteContractModelDraftRunMutation();
  const [showCreate, setShowCreate] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const openRun = (id: string) => navigate(`/contract-model-drafts/${id}`);

  // Offered only on a canceled run with no recommendations; the server is the authority (409 + reason).
  const handleDeleteRun = (run: IDraftRunSummary) => {
    confirmDialog({
      header: "Delete this run?",
      message:
        "This removes the run and its generated output. Documents attached to the contract are kept.",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Delete run",
      rejectLabel: "Keep it",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteRun(run.id).unwrap();
          toastRef?.current?.show({
            severity: "success",
            summary: "Run deleted",
            life: 4000,
          });
        } catch (e) {
          toastRef?.current?.show({
            severity: "error",
            summary: "Couldn't delete",
            detail: errorDetail(e),
            life: 7000,
          });
        }
      },
    });
  };

  const actionsBody = (r: IDraftRunSummary) =>
    canDeleteRun(r) ? (
      <Button
        label="Delete run"
        icon="pi pi-trash"
        className="p-button-text p-button-sm p-button-danger"
        disabled={deleting}
        onClick={(e) => {
          // The row itself opens the run.
          e.stopPropagation();
          handleDeleteRun(r);
        }}
      />
    ) : null;

  return (
    <div className="p-4">
      <div className="flex justify-content-between align-items-center mb-3">
        <h1 className="m-0">AI Contract Modeling</h1>
        {activeTab === 0 && (
          <div className="flex align-items-center gap-3">
            <label
              htmlFor="show-inactive-runs"
              className="flex align-items-center gap-2 text-700"
            >
              <InputSwitch
                inputId="show-inactive-runs"
                checked={showInactive}
                onChange={(e) => setShowInactive(!!e.value)}
              />
              Show canceled and failed
            </label>
            <Button
              label="New contract model"
              icon="pi pi-plus"
              onClick={() => setShowCreate(true)}
            />
          </div>
        )}
      </div>

      <TabView
        activeIndex={activeTab}
        onTabChange={(e) => setActiveTab(e.index)}
      >
        <TabPanel header="Runs">
          <PageDataTable
            value={[...runs]}
            dataKey="id"
            emptyMessage="No runs yet. Click “New contract model” to model a contract."
            rowClassName={() => "cursor-pointer"}
            onRowClick={(e) => openRun((e.data as IDraftRunSummary).id)}
          >
            <Column header="Name" body={nameBody} />
            <Column header="Status" body={statusBody} />
            <Column header="Documents" field="documentCount" />
            <Column header="Recommendations" field="recommendationCount" />
            <Column header="Created" body={createdBody} />
            <Column body={actionsBody} style={{ width: "9rem" }} />
          </PageDataTable>
        </TabPanel>
        <TabPanel header="Standing instructions">
          <StandingInstructionsTab />
        </TabPanel>
      </TabView>

      <NewContractModelDialog
        visible={showCreate}
        onHide={() => setShowCreate(false)}
      />
    </div>
  );
}
